import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Rnd } from 'react-rnd';
import { dispatch } from '../../../../redux/store';
import { setSaveIsDisabled } from '../../../../redux/slices/thresholdFormSlice';
import FieldChip from './FieldChip';
import {
  BASE_H,
  BASE_W,
  LINE_HEIGHT,
  MAX_FONT_PT,
  MIN_FONT_PT,
  PAGE_H_MM,
  PAGE_W_MM,
  clampMm,
  mmToPx,
  ptToPx,
  pxToMmX,
  pxToMmY,
  pxToPt,
} from './coords';
import {
  columnWidthsOf,
  rowHeightsOf,
  roundSizes,
  totalOf,
  withSize,
} from '../../matrixGrid';
import { rebuildMatrix } from '../../matrixItems';
import { formFontPt } from '../thresholdConstants';
import { BuilderElement, buildElements, moveElement, moveGroupBy, resizeElement } from './elements';
import { buildFieldBoxes, computeFitWidths, FIELD_RIGHT_MARGIN_MM } from '../../fillLayout';

const SNAP_TH = 6; // px
const MM_W = (mm: number) => (mm / PAGE_W_MM) * BASE_W;
const MM_H = (mm: number) => (mm / PAGE_H_MM) * BASE_H;

const GRIP = {
  background: 'var(--primary-600)',
  border: '1px solid #fff',
  borderRadius: '2px',
  width: '10px',
  height: '10px',
  boxShadow: 'var(--shadow-xs)',
};
const RESIZE_HANDLE_STYLES: Record<string, React.CSSProperties> = {
  bottomRight: { ...GRIP, right: '-5px', bottom: '-5px' },
  right: { ...GRIP, right: '-5px', top: 'calc(50% - 5px)' },
  bottom: { ...GRIP, bottom: '-5px', left: 'calc(50% - 5px)' },
};

interface Props {
  data: Record<string, any>;
  setData: (d: Record<string, any>) => void;
  tab: string;
  pages: Array<{ base64: string }> | undefined;
  selectedKey: string | null;
  setSelectedKey: (k: string | null) => void;
  zoom: number;
}

interface Geom {
  x: number;
  y: number;
  w: number;
  h: number;
}

// Baseline elements are lifted by their own font ascent so the visible glyph
// baseline sits on the stored coordinate.
const ascentPx = (el: BuilderElement) => ptToPx(el.fontPt) * 0.8;

// Chip footprint + top-left px for an element. `fitWidths` supplies the
// column-clamped box width used by wrapped fields, matching overlayInfo.
const geom = (el: BuilderElement, fitWidths: Record<string, number> = {}): Geom => {
  const anchorPx = mmToPx(el.leftMm, el.topMm);
  if (el.kind === 'signature') {
    return { x: anchorPx.x, y: anchorPx.y, w: MM_W(el.wMm || 40), h: MM_H(el.hMm || 10) };
  }
  if (el.kind === 'matrix') {
    // The full grid. This used to stop at the LAST CELL'S ORIGIN — one column
    // and one row short of the table — so the overlay never covered the printed
    // box it was being lined up against, which is precisely what made the
    // alignment guesswork.
    const wMm = Math.max(totalOf(el.colWidthsMm || []), 8);
    const hMm = Math.max(totalOf(el.rowHeightsMm || []), 6);
    return { x: anchorPx.x, y: anchorPx.y - ascentPx(el), w: MM_W(wMm), h: MM_H(hMm) };
  }
  const fpx = ptToPx(el.fontPt);
  if (el.kind === 'checkbox') {
    // A radio/checkbox option is a small square control sized to the font.
    const s = fpx * 1.5;
    return { x: anchorPx.x, y: anchorPx.y - ascentPx(el), w: s, h: s };
  }
  if (el.kind === 'field' && el.multiline) {
    // A wrapped field occupies its real PDF box: the explicit width, else the
    // column-clamped one, else out to the page margin — and as many lines tall
    // as Max lines allows. Seeing that footprint here is the point: it shows the
    // admin exactly how far down the wrapped text can reach.
    const wMm =
      el.wMm && el.wMm > 0
        ? el.wMm
        : fitWidths[el.key] || PAGE_W_MM - el.leftMm - FIELD_RIGHT_MARGIN_MM;
    const lines = el.maxLines || 1;
    return {
      x: anchorPx.x,
      y: anchorPx.y - ascentPx(el),
      w: MM_W(wMm),
      h: fpx * LINE_HEIGHT * lines,
    };
  }

  // A single-line field shows the SAME box the inspector will type into: its
  // explicit width, else the column-clamped one, else out to the page margin.
  //
  // It used to be auto-sized to the length of the placeholder text, which had
  // nothing to do with the real box — so the builder showed a snug little chip
  // for a field the inspector then saw running all the way to the next field on
  // the row. Drag the right edge to set a real width; that is respected here,
  // on the inspector's canvas and in the PDF alike.
  const wMm =
    el.wMm && el.wMm > 0
      ? el.wMm
      : fitWidths[el.key] || PAGE_W_MM - el.leftMm - FIELD_RIGHT_MARGIN_MM;
  return {
    x: anchorPx.x,
    y: anchorPx.y - ascentPx(el),
    w: MM_W(wMm),
    h: fpx * LINE_HEIGHT,
  };
};

// Convert a chip's dragged top-left px back to the stored mm anchor.
const toAnchorMm = (el: BuilderElement, xPx: number, yPx: number) => {
  const leftMm = clampMm(pxToMmX(xPx), PAGE_W_MM);
  const baseYpx = el.anchor === 'baseline' ? yPx + ascentPx(el) : yPx;
  const topMm = clampMm(pxToMmY(baseYpx), PAGE_H_MM);
  return { leftMm, topMm };
};

const toSrc = (b64: string) => (b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`);

const FormBuilderCanvas = (props: Props) => {
  const { data, setData, tab, pages, selectedKey, setSelectedKey, zoom } = props;
  const stageRef = useRef<HTMLDivElement>(null);
  const [guides, setGuides] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] });

  const elements = useMemo(
    () => buildElements(data[tab] || {}, formFontPt(data)),
    [data, tab],
  );

  // Same column-clamped widths overlayInfo uses for wrapped fields, so the chip
  // on the canvas matches the box the PDF will actually wrap inside.
  const fitWidths = useMemo(() => computeFitWidths(buildFieldBoxes(data, tab)), [data, tab]);
  const geomOf = (el: BuilderElement) => geom(el, fitWidths);

  const commit = (next: Record<string, any>) => {
    setData(next);
    dispatch(setSaveIsDisabled(false));
  };

  // The individual element the user grabbed. The properties panel selects by
  // FIELD KEY, but a checkbox group is one key with many boxes — so a key alone
  // cannot say which box is active, and nudging moved the whole group while
  // dragging moved a single box. Tracking the id keeps the two consistent.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Falls back to the group's first box when the selection came from the
  // properties panel, which only knows the key.
  const activeId = useMemo(() => {
    if (selectedId && elements.some(el => el.id === selectedId && el.key === selectedKey)) {
      return selectedId;
    }
    const first = elements.find(el => el.key === selectedKey);
    return first ? first.id : null;
  }, [selectedId, selectedKey, elements]);

  // Select an element and focus the stage so arrow-key nudging works immediately.
  const select = (el: BuilderElement) => {
    setSelectedKey(el.key);
    setSelectedId(el.id);
    if (stageRef.current) stageRef.current.focus();
  };

  const rawSrc = pages && pages[Number(tab)] && pages[Number(tab)].base64;
  const imgSrc = rawSrc ? toSrc(rawSrc) : '';

  // Candidate snap lines = every OTHER element's edges/centres, plus page centre.
  const snapLines = (movingId: string) => {
    const xs: number[] = [BASE_W / 2];
    const ys: number[] = [BASE_H / 2];
    elements.forEach(el => {
      if (el.id === movingId) return;
      const g = geomOf(el);
      xs.push(g.x, g.x + g.w / 2, g.x + g.w);
      ys.push(g.y, g.y + g.h / 2, g.y + g.h);
    });
    return { xs, ys };
  };

  const bestSnap = (edgePositions: number[], candidates: number[]) => {
    let best: { snap: number; guide: number; dist: number } | null = null;
    edgePositions.forEach((edge, idx) => {
      candidates.forEach(c => {
        const dist = Math.abs(edge - c);
        if (dist <= SNAP_TH && (!best || dist < best.dist)) {
          // snap so this edge lands on the candidate; offset back to top-left
          const offset = [0, 0.5, 1][idx];
          best = { snap: c, guide: c, dist, offset } as any;
        }
      });
    });
    return best as null | { snap: number; guide: number; dist: number; offset: number };
  };

  const resolveSnap = (x: number, y: number, w: number, h: number, movingId: string) => {
    const { xs, ys } = snapLines(movingId);
    const sx = bestSnap([x, x + w / 2, x + w], xs);
    const sy = bestSnap([y, y + h / 2, y + h], ys);
    return {
      x: sx ? sx.snap - sx.offset * w : x,
      y: sy ? sy.snap - sy.offset * h : y,
      guideX: sx ? [sx.guide] : [],
      guideY: sy ? [sy.guide] : [],
    };
  };

  const onDrag = (el: BuilderElement, g: Geom) => (_e: any, d: { x: number; y: number }) => {
    const r = resolveSnap(d.x, d.y, g.w, g.h, el.id);
    setGuides({ x: r.guideX, y: r.guideY });
  };

  const onDragStop = (el: BuilderElement, g: Geom) => (_e: any, d: { x: number; y: number }) => {
    setGuides({ x: [], y: [] });
    const r = resolveSnap(d.x, d.y, g.w, g.h, el.id);
    const { leftMm, topMm } = toAnchorMm(el, r.x, r.y);
    commit(moveElement(data, tab, el, leftMm, topMm));
  };

  // Resize maps to: box size (signature), grid spacing (table); for a text field
  // the RIGHT edge sets its width (shrink-to-fit box) and the BOTTOM edge sets
  // font size; checkboxes resize font. Handles are bottom/right only, so the
  // top-left origin stays put and no repositioning is needed.
  const onResizeStop = (el: BuilderElement) => (_e: any, dir: string, ref: HTMLElement) => {
    const wpx = ref.offsetWidth;
    const hpx = ref.offsetHeight;
    const toFontPt = () => {
      const raw = Math.round(pxToPt(hpx / LINE_HEIGHT));
      return Math.min(MAX_FONT_PT, Math.max(MIN_FONT_PT, raw));
    };
    if (el.kind === 'signature') {
      commit(resizeElement(data, tab, el, { wMm: pxToMmX(wpx), hMm: pxToMmY(hpx) }));
    } else if (el.kind === 'matrix') {
      commit(resizeElement(data, tab, el, {
        totalWMm: pxToMmX(wpx),
        totalHMm: pxToMmY(hpx),
      }));
    } else if (el.kind === 'field' && dir === 'right') {
      commit(resizeElement(data, tab, el, { widthMm: pxToMmX(wpx) }));
    } else {
      commit(resizeElement(data, tab, el, { fontPt: toFontPt() }));
    }
  };

  // Drag a column/row divider to resize just that track.
  //
  // This is what makes a table match a scanned form: the printed columns are
  // uneven, so nudging one boundary at a time is the only way to land the cells
  // in the right boxes. Live rather than on mouse-up, because the whole point is
  // watching the line meet the printed rule.
  const onTrackResizeStart = (el: BuilderElement) => (
    axis: 'col' | 'row',
    index: number,
    e: React.MouseEvent,
  ) => {
    const startClient = axis === 'col' ? e.clientX : e.clientY;
    const sizes = axis === 'col'
      ? columnWidthsOf(data[tab][el.key])
      : rowHeightsOf(data[tab][el.key]);
    const startMm = sizes[index];
    if (startMm === undefined) return;

    select(el);
    let frame: number | null = null;
    let latest = startMm;

    const apply = () => {
      frame = null;
      const field = data[tab][el.key];
      if (!field) return;
      const next = axis === 'col'
        ? { ...field, columnWidths: roundSizes(withSize(columnWidthsOf(field), index, latest)) }
        : { ...field, rowHeights: roundSizes(withSize(rowHeightsOf(field), index, latest)) };
      commit({
        ...data,
        [tab]: { ...data[tab], [el.key]: { ...next, matrix: rebuildMatrix(next) } },
      });
    };

    const onMove = (ev: MouseEvent) => {
      // The stage is CSS-scaled, so screen pixels are zoom times base pixels.
      const deltaPx = ((axis === 'col' ? ev.clientX : ev.clientY) - startClient) / (zoom || 1);
      const deltaMm = axis === 'col' ? pxToMmX(deltaPx) : pxToMmY(deltaPx);
      latest = startMm + deltaMm;
      if (frame === null) frame = requestAnimationFrame(apply);
    };

    const onUp = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      apply();
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const removeField = (key: string) => {
    const page = { ...data[tab] };
    delete page[key];
    commit({ ...data, [tab]: page });
    if (selectedKey === key) setSelectedKey(null);
  };

  // Keyboard nudge of the selected element (arrows = 1mm, Shift+arrows = 10mm).
  // Alt+arrows move a whole checkbox/radio group at once — that used to be what
  // a plain arrow did, which meant clicking one box and nudging dragged every
  // other box along with it while the mouse moved boxes one at a time.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!selectedKey) return;
    const dirs: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const dir = dirs[e.key];
    if (!dir) return;
    e.preventDefault();
    const step = e.shiftKey ? 10 : 1;
    const dLeft = dir[0] * step;
    const dTop = dir[1] * step;
    const field = data[tab] && data[tab][selectedKey];
    if (e.altKey && field && field.inputType && Array.isArray(field.items)) {
      commit(moveGroupBy(data, tab, selectedKey, dLeft, dTop));
      return;
    }
    const el = elements.find(x => x.id === activeId);
    if (el) {
      const nextLeft = clampMm(el.leftMm + dLeft, PAGE_W_MM);
      const nextTop = clampMm(el.topMm + dTop, PAGE_H_MM);
      commit(moveElement(data, tab, el, nextLeft, nextTop));
    }
  };

  return (
    <Scroll>
      <StageOuter style={{ width: BASE_W * zoom, height: BASE_H * zoom }}>
        <Stage
          ref={stageRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseDown={e => {
            if (e.target === stageRef.current) {
              setSelectedKey(null);
              setSelectedId(null);
            }
          }}
          style={{ width: BASE_W, height: BASE_H, transform: `scale(${zoom})` }}
        >
          {imgSrc ? <PageImg src={imgSrc} alt={`Page ${Number(tab) + 1}`} draggable={false} /> : <NoPage>No page image</NoPage>}

          {guides.x.map(gx => (
            <GuideV key={`gx${gx}`} style={{ left: gx }} />
          ))}
          {guides.y.map(gy => (
            <GuideH key={`gy${gy}`} style={{ top: gy }} />
          ))}

          {elements.map(el => {
            const g = geomOf(el);
            // Per ELEMENT, not per key: selecting one box of a checkbox group
            // used to light up (and hand resize grips to) every box in it. The
            // group still reads as a unit via the dimmer `related` state.
            const isSelected = el.id === activeId;
            const isRelated = !isSelected && el.key === selectedKey;
            let handles: any = false;
            if (isSelected) {
              if (el.kind === 'field') handles = { right: true, bottom: true };
              else if (el.kind === 'checkbox') handles = { bottomRight: true };
              else handles = { right: true, bottom: true, bottomRight: true };
            }
            return (
              <Rnd
                key={el.id}
                bounds="parent"
                scale={zoom}
                enableResizing={handles}
                lockAspectRatio={el.kind === 'checkbox'}
                minWidth={10}
                minHeight={8}
                resizeHandleStyles={RESIZE_HANDLE_STYLES}
                size={{ width: g.w, height: g.h }}
                position={{ x: g.x, y: g.y }}
                onDrag={onDrag(el, g)}
                onDragStart={() => select(el)}
                onDragStop={onDragStop(el, g)}
                onResizeStart={() => select(el)}
                onResizeStop={onResizeStop(el)}
                style={{ zIndex: isSelected ? 2 : 1 }}
              >
                <div
                  role="button"
                  tabIndex={-1}
                  onMouseDown={() => select(el)}
                  style={{ width: '100%', height: '100%' }}
                >
                  <FieldChip
                    el={el}
                    selected={isSelected}
                    related={isRelated}
                    required={!!(data[tab] && data[tab][el.key] && data[tab][el.key].required)}
                    onDelete={() => removeField(el.key)}
                    onTrackResizeStart={onTrackResizeStart(el)}
                  />
                </div>
              </Rnd>
            );
          })}
        </Stage>
      </StageOuter>
    </Scroll>
  );
};

export default FormBuilderCanvas;

const Scroll = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 72vh;
  overflow: auto;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
`;

const StageOuter = styled.div`
  position: relative;
  /* margin auto centers the page when it fits, and degrades to left/top
     aligned (fully scrollable) when zoomed larger than the viewport — unlike
     flex 'justify-content: center', which makes the overflow unreachable. */
  margin: auto;
`;

const Stage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  background: var(--neutral-0);
  box-shadow: var(--shadow-md);
  outline: none;
`;

const PageImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  user-select: none;
  pointer-events: none;
`;

const NoPage = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const GuideV = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--primary-600);
  pointer-events: none;
  z-index: 5;
`;

const GuideH = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--primary-600);
  pointer-events: none;
  z-index: 5;
`;
